import { Icon, type IconName } from "@/components/common/Icon";

interface ConfirmButtonProps {
	label: string;
	loading: boolean;
	type?: "submit" | "button";
	disabled?: boolean;
	variant?: "primary" | "secondary" | "outline" | "destructive" | "ghost";
	size?: "sm" | "md";
	icon?: IconName;
	/** 完了を短時間チェックマークで示す。ラベルは幅を保つため残したまま透明にする。 */
	succeeded?: boolean;
	onClick?: () => void;
	"data-testid"?: string;
}

const sizes = {
	sm: {
		height: 28,
		padding: "0 12px",
		fontSize: "var(--text-caption)",
		borderRadius: "var(--radius-2)",
		spinnerSize: 10,
		spinnerBorder: 1.5,
	},
	md: {
		height: 34,
		padding: "0 16px",
		fontSize: "var(--text-ui)",
		borderRadius: "var(--radius-3)",
		spinnerSize: 14,
		spinnerBorder: 2,
	},
};

/**
 * 見た目の状態 (通常・hover・active) はここに集約する。背景や文字色を inline style に書くと
 * hover / active のクラスが inline に負けて効かなくなるため、色は必ずクラスで指定する。
 * hover / active は enabled: を付け、無効時に反応しないようにする。
 */
const variants = {
	primary: {
		className:
			"bg-accent-solid text-on-accent-solid enabled:hover:bg-accent-solid-hover enabled:active:brightness-90",
		boxShadow: "none",
		spinnerBorder: "var(--fg-3)",
		spinnerTopColor: "var(--on-accent-solid)",
		fontWeight: 600,
	},
	// hover で背景が明るくなると枠線との明暗差が縮み、枠が細く見える。hover / active 中は枠も
	// 強めて明暗差を保つ。枠をクラスで切り替えるため、inline の boxShadow は持たない。
	secondary: {
		className:
			"bg-surface-1 text-fg-2 shadow-ring enabled:hover:bg-overlay-hover enabled:hover:shadow-ring-strong enabled:active:bg-overlay-active enabled:active:shadow-ring-strong",
		boxShadow: undefined,
		spinnerBorder: "var(--line-1)",
		spinnerTopColor: "var(--fg-2)",
		fontWeight: 500,
	},
	// 背景を塗らない secondary。ホバーで色が変わるカードの上に置いても下地に馴染む。
	outline: {
		className:
			"bg-transparent text-fg-1 disabled:text-fg-3 shadow-ring enabled:hover:bg-overlay-hover enabled:hover:shadow-ring-strong enabled:active:bg-overlay-active",
		boxShadow: undefined,
		spinnerBorder: "var(--line-1)",
		spinnerTopColor: "var(--fg-1)",
		fontWeight: 500,
	},
	destructive: {
		className:
			"bg-danger-solid text-on-danger-solid enabled:hover:bg-danger-solid-hover enabled:active:brightness-90",
		boxShadow: "none",
		spinnerBorder: "var(--fg-3)",
		spinnerTopColor: "var(--on-danger-solid)",
		fontWeight: 600,
	},
	// 塗りのないボタン (ダイアログのキャンセル・閉じる)。
	ghost: {
		className:
			"bg-transparent text-fg-2 enabled:hover:bg-overlay-hover enabled:active:bg-overlay-active",
		boxShadow: "none",
		spinnerBorder: "var(--line-1)",
		spinnerTopColor: "var(--fg-2)",
		fontWeight: 500,
	},
};

/**
 * ラベル・確認中表示・無効状態・hover / active を統一した操作ボタン。ダイアログのフッターは
 * 主操作を primary / destructive、キャンセルを ghost で組み、生の button を書かない。
 * secondary は shadow-ring を使う控えめなボタン。
 */
export function ConfirmButton({
	label,
	loading,
	type = "button",
	disabled,
	variant = "primary",
	size = "md",
	icon,
	succeeded = false,
	onClick,
	"data-testid": testId,
}: ConfirmButtonProps) {
	const { height, padding, fontSize, borderRadius, spinnerSize, spinnerBorder } = sizes[size];
	const variantStyle = variants[variant];

	return (
		<button
			type={type}
			disabled={disabled || loading}
			onClick={onClick}
			data-testid={testId}
			className={`${variantStyle.className} transition-[color,background-color,box-shadow]`}
			style={{
				height,
				// 置き場所が狭いときはラベルを「…」で省略して縮む。
				minWidth: 0,
				maxWidth: "100%",
				padding,
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				border: 0,
				boxShadow: variantStyle.boxShadow,
				borderRadius,
				fontSize,
				fontWeight: variantStyle.fontWeight,
				fontFamily: "var(--font-sans)",
				cursor: loading || disabled ? "not-allowed" : "pointer",
				opacity: disabled ? 0.5 : loading ? 0.7 : 1,
			}}
		>
			<span
				style={{
					position: "relative",
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					minWidth: 0,
				}}
			>
				<span
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: icon ? 6 : 0,
						minWidth: 0,
						opacity: loading || succeeded ? 0 : 1,
					}}
				>
					{icon && <Icon name={icon} size={14} />}
					<span
						style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
						// 無効な button の中にもホバーは届くため、無効時はラベルを出さず外側の要素の理由を見せる。
						data-tooltip={disabled || loading ? undefined : label}
						data-tooltip-if-truncated
					>
						{label}
					</span>
				</span>
				<span
					style={{
						position: "absolute",
						display: "inline-flex",
						opacity: succeeded ? 1 : 0,
					}}
				>
					<Icon name="check" size={14} />
				</span>
				<span
					style={{
						position: "absolute",
						display: "inline-block",
						width: spinnerSize,
						height: spinnerSize,
						borderRadius: "var(--radius-full)",
						border: `${spinnerBorder}px solid ${variantStyle.spinnerBorder}`,
						borderTopColor: variantStyle.spinnerTopColor,
						animation: "izanagi-spin 0.9s linear infinite",
						opacity: loading ? 1 : 0,
					}}
				/>
			</span>
		</button>
	);
}
